import getFilter from './utils/getFilter';
import isPowerOf2 from './utils/isPowerOf2';

class FrameBuffer {

  constructor ( gl, options ) {

    
    this.options = Object.assign({},{
      format: gl.RGBA,
      type:   gl.UNSIGNED_BYTE,
      linear: true,
      mipmap: false,
      mipmapLinear: false,
      wrapS: gl.CLAMP_TO_EDGE,
      wrapT: gl.CLAMP_TO_EDGE,
      depthTexture: null
    }, options);

    this.gl = gl;
      
    this.width  = this.options.width;
    this.height = this.options.height;
    this.format = this.options.format;
    this.type   = this.options.type;
    this.linear = this.options.linear;
    this.mipmap = this.options.mipmap;
    this.mipmapLinear = this.options.mipmapLinear;

    if (this.type == gl.FLOAT ) {
      var floatTextures = gl.getExtension('OES_texture_float');
      var floatTextureLinearFiltering = gl.getExtension('OES_texture_float_linear');
      if (!floatTextures ||  window.innerWidth < 768) {
        // console.warn('trying to create a FrameBuffer of with gl.FLOAT type but there\s no floating point texture support. trying HALF_FLOAT');
        this.type = "HALF_FLOAT"
      }
    }

    if (this.type == "HALF_FLOAT") {
      var halfFloatTexturesExt = gl.getExtension('OES_texture_half_float');
      var halfFloatTextureLinearFiltering = gl.getExtension('OES_texture_half_float_linear');
      if (!halfFloatTexturesExt) {
        console.warn('trying to create a texture of with gl.HALF_FLOAT type but there\s no half floating point texture support; falling back to UNSIGNED_BYTE type');
        this.type = gl.UNSIGNED_BYTE;
      }
      else {
        this.type = halfFloatTexturesExt.HALF_FLOAT_OES;
        this.isHalfFloat = true;
      }
    }

    this._texture = this.gl.createTexture();
    this.gl.bindTexture(this.gl.TEXTURE_2D, this._texture);

    if (this.type == this.gl.FLOAT) {
      this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.format, this.width, this.height, 0, this.format, this.type, null);//new Float32Array( new Array(this.width * this.height * 4) ));
    }
    else if (this.isHalfFloat) {
      this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.format, this.width, this.height, 0, this.format, this.type, null);
    }
    else {
      this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.format, this.width, this.height, 0, this.format, this.type, null);//new Uint8Array( new Array(this.width * this.height * 4) ));
    }
    
    this.wrapS = this.options.wrapS;
    this.wrapT = this.options.wrapT;
    
    var isPOT = isPowerOf2(this.width) && isPowerOf2(this.height);
    if (!isPOT) {
      //not POT textures can't repeat & cant use mipmap
      this.wrapS = gl.CLAMP_TO_EDGE;
      this.wrapT = gl.CLAMP_TO_EDGE;
      this.mipmap       = false;
      this.mipmapLinear = false;
    }

    this.gl.bindTexture(this.gl.TEXTURE_2D, this._texture);
    this.gl.texParameteri( this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.wrapS );
    this.gl.texParameteri( this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.wrapT );

    //nPOT texture cannot mipmap
    this.setFilter( this.linear, this.mipmap, this.mipmapLinear );

    if (!this.options.depthTexture) {
      this.renderbuffer = this.gl.createRenderbuffer();
      this.gl.bindRenderbuffer(this.gl.RENDERBUFFER, this.renderbuffer);
      this.gl.renderbufferStorage(this.gl.RENDERBUFFER, this.gl.DEPTH_COMPONENT16, this.width, this.height);
    }
    
    this.fbo = this.gl.createFramebuffer();
    this.gl.bindFramebuffer( this.gl.FRAMEBUFFER, this.fbo );
    this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, this._texture, 0);
    
    if (this.options.depthTexture) {
      this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.DEPTH_ATTACHMENT, this.gl.TEXTURE_2D, this.options.depthTexture._texture, 0);
    }
    else {
      this.gl.framebufferRenderbuffer(this.gl.FRAMEBUFFER, this.gl.DEPTH_ATTACHMENT, this.gl.RENDERBUFFER, this.renderbuffer);
    }

    this.unbind();

  } 

  /**
   * [setFilter description]
   * @param {[type]} linear       [description]
   * @param {[type]} mipmap       [description]
   * @param {[type]} mipmapLinear [description]
   */
  setFilter(linear, mipmap, mipmapLinear) {
    var gl = this.gl;
    var filter = getFilter( !!linear, !!mipmap, !!mipmapLinear);
    gl.bindTexture(   gl.TEXTURE_2D, this._texture);
    gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, getFilter( !!linear, false, false ) );
    gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter );
  }

  resize(w, h) {

    if( this.width !== w || this.height !== h ) {
      this.width  = w|0;
      this.height = h|0;
    }

    var isPOT = isPowerOf2(this.width) && isPowerOf2(this.height);
    if (!isPOT) {
      this.wrapS  = this.gl.CLAMP_TO_EDGE;
      this.wrapT  = this.gl.CLAMP_TO_EDGE;
      this.mipmap = false;
      this.mipmapLinear = false;
      this.setFilter( this.linear, this.mipmap, this.mipmapLinear );
    }

    this.gl.bindTexture(this.gl.TEXTURE_2D, this._texture);
    // this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.format, this.width, this.height, 0, this.format, this.type, new Uint8Array( new Array(this.width * this.height * 4) ));
    
    if (this.type == this.gl.FLOAT) {
      this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.format, this.width, this.height, 0, this.format, this.type, null);//new Float32Array( new Array(this.width * this.height * 4) ));
    }
    else if (this.isHalfFloat) {
      this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.format, this.width, this.height, 0, this.format, this.type, null);
    }
    else {
      this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.format, this.width, this.height, 0, this.format, this.type, null);//new Uint8Array( new Array(this.width * this.height * 4) ));
    }
    
    if (this.options.depthTexture) {
      this.depthTexture.resize(this.width, this.height);
      // this.gl.bindTexture(this.gl.TEXTURE_2D, this._depthTexture);
      // this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.DEPTH_COMPONENT, this.width, this.height, 0, this.gl.DEPTH_COMPONENT, this.gl.UNSIGNED_SHORT, null);
    }

    this.gl.bindFramebuffer( this.gl.FRAMEBUFFER, this.fbo );
    this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, this._texture, 0);
    
    if (this.options.depthTexture) {
      this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.DEPTH_ATTACHMENT, this.gl.TEXTURE_2D, this.options.depthTexture._texture, 0);
    }
    else {
      this.gl.bindRenderbuffer(this.gl.RENDERBUFFER, this.renderbuffer);
      this.gl.renderbufferStorage(this.gl.RENDERBUFFER, this.gl.DEPTH_COMPONENT16, this.width, this.height);
      this.gl.framebufferRenderbuffer(this.gl.FRAMEBUFFER, this.gl.DEPTH_ATTACHMENT, this.gl.RENDERBUFFER, this.renderbuffer);
    }
    
    this.unbind();
  }

  bindFrame() {
    if (!this.skipViewport) {
      this.gl.viewport( 0, 0, this.width, this.height );
    }
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.fbo);
    if (!this.options.depthTexture) {
      this.gl.bindRenderbuffer(this.gl.RENDERBUFFER, this.renderbuffer);
    }
    else {
      this.gl.bindRenderbuffer(this.gl.RENDERBUFFER, this.renderbuffer);
      this.gl.renderbufferStorage(this.gl.RENDERBUFFER, this.gl.DEPTH_COMPONENT16, this.width, this.height);
      this.gl.framebufferRenderbuffer(this.gl.FRAMEBUFFER, this.gl.DEPTH_ATTACHMENT, this.gl.RENDERBUFFER, this.renderbuffer);
    }
    this.clear();
  }
  
  bind(unit) {
    if (unit !== void 0) {
      this.gl.activeTexture( this.gl.TEXTURE0 + (0|unit) );
    }
    // else {
    //   this.gl.activeTexture( this.gl.TEXTURE0 );
    // }
    this.gl.bindTexture(this.gl.TEXTURE_2D, this._texture);
  }

  unbind() {
    this.gl.bindTexture(this.gl.TEXTURE_2D, null);
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
    if (!this.options.depthTexture) {
      this.gl.bindRenderbuffer(this.gl.RENDERBUFFER, null);
    }
  }

  clear(color, depth, stencil) {
    var bits = 0;
    if ( color === void 0 || color ) bits |= this.gl.COLOR_BUFFER_BIT;
    if ( depth === void 0 || depth ) bits |= this.gl.DEPTH_BUFFER_BIT;
    if ( stencil === void 0 || stencil ) bits |= this.gl.STENCIL_BUFFER_BIT;
    this.gl.clear( bits );
  }

  dispose() {
    this.gl.deleteFramebuffer( this.fbo );
    this.fbo   = null;
    this.gl    = null;
  }

}


export default FrameBuffer;
